import { RoutePath } from '../../../../features/routes'
import { Branch } from '../../../manager'
import { DeliveryStatus } from '../../../deliveries'
import { DashboardDataSource, DashboardScope, DashboardWidgetDefinition } from '../../types'
import { AdminSalesData } from '../../hooks/admin/useAdminSalesData'
import { AdminOpsData } from '../../hooks/admin/useAdminOpsData'
import { ManagerFinanceData } from '../../hooks/admin/useManagerFinanceData'
import { SalesKpis } from '../../hooks/admin/useSalesKpis'
import { WarehouseData } from '../../hooks/warehouse/useWarehouseData'
import { LogisticsData } from '../../hooks/logistics/useLogisticsData'
import { PurchasesData } from '../../hooks/purchases/usePurchasesData'
import { calcTrend, formatAmount, getMonthRange, isInRange } from '../../utils/dashboardUtils'
import { WidgetCard } from '../widgets/widget-card/widgetCard'
import { KpiCard } from '../widgets/kpi-card/kpiCard'
import { SalesChart } from '../widgets/sales-chart/salesChart'
import { CriticalStockWidget } from '../widgets/critical-stock/criticalStockWidget'
import { TopRotationWidget } from '../widgets/top-rotation/topRotationWidget'
import { ChecksAlertWidget } from '../widgets/checks-alert/checksAlertWidget'
import { TopDebtorsWidget } from '../widgets/top-debtors/topDebtorsWidget'
import { ExpiringHoardsWidget } from '../widgets/expiring-hoards/expiringHoardsWidget'
import { SellerRankingWidget } from '../widgets/seller-ranking/sellerRankingWidget'
import { CashFundsWidget } from '../widgets/cash-funds/cashFundsWidget'
import { CashBalanceWidget } from '../widgets/cash-balance/cashBalanceWidget'
import { OverduePurchasesWidget } from '../widgets/overdue-purchases/overduePurchasesWidget'
import { RoadmapsTodayWidget } from '../widgets/roadmaps-today/roadmapsTodayWidget'

type DataState<T> = {
    loading: boolean
    error: boolean
    data: T | null
}

// Data of the cards, already limited to the scope of the role (own, branch or company operations)
export type DashboardData = {
    scope: DashboardScope
    branchId?: string
    sales: DataState<AdminSalesData>
    operations: DataState<AdminOpsData>
    finance: DataState<ManagerFinanceData>
    warehouse: DataState<WarehouseData>
    logistics: DataState<LogisticsData>
    purchases: DataState<PurchasesData>
    salesKpis: SalesKpis
    branches: Branch[]
}

const isMine = (scope: DashboardScope) => scope === DashboardScope.MINE
const plural = (count: number, singular: string, pluralWord: string) => `${count} ${count === 1 ? singular : pluralWord}`

let widgetsCatalog: Record<string, DashboardWidgetDefinition<DashboardData>> | undefined

// Every card of the home. Which ones each role gets (and which ones by default) is in dashboardRoles.
// It's built the first time it's used (not when the file loads): the routes module imports the dashboard,
// so RoutePath isn't defined yet while this file is being loaded
export const getDashboardWidgets = (): Record<string, DashboardWidgetDefinition<DashboardData>> => {
    if (!widgetsCatalog) {
        widgetsCatalog = buildDashboardWidgets()
    }
    return widgetsCatalog
}

const buildDashboardWidgets = (): Record<string, DashboardWidgetDefinition<DashboardData>> => {
    const { SALES, OPERATIONS, FINANCE, WAREHOUSE, LOGISTICS, PURCHASES } = DashboardDataSource
    return {
        // Sales and collections
        salesMonth: {
            id: 'salesMonth',
            title: 'Ventas del mes',
            description: 'Ventas + acopios del mes, comparado con el mes anterior',
            defaultSize: 's',
            sources: [SALES],
            render: ({ sales, salesKpis, scope }) => (
                <WidgetCard title={isMine(scope) ? 'Mis ventas del mes' : 'Ventas del mes'} loading={sales.loading} error={sales.error}>
                    <KpiCard
                        value={formatAmount(salesKpis.salesMonth)}
                        label="ventas + acopios"
                        trend={calcTrend(salesKpis.salesMonth, salesKpis.salesPrevMonth)}
                    />
                </WidgetCard>
            )
        },
        salesToday: {
            id: 'salesToday',
            title: 'Ventas de hoy',
            description: 'Monto y cantidad de ventas del día, comparado con el mismo día de la semana pasada',
            defaultSize: 's',
            sources: [SALES],
            linkPath: RoutePath.SALES,
            render: ({ sales, salesKpis, scope }) => (
                <WidgetCard title={isMine(scope) ? 'Mis ventas de hoy' : 'Ventas de hoy'} loading={sales.loading} error={sales.error} linkLabel="Ver ventas" linkPath={RoutePath.SALES}>
                    <KpiCard
                        value={formatAmount(salesKpis.salesToday)}
                        count={String(salesKpis.salesTodayCount)}
                        label="ventas del día"
                        trend={calcTrend(salesKpis.salesToday, salesKpis.salesSameDayLastWeek)}
                        trendLabel="vs semana pasada"
                    />
                </WidgetCard>
            )
        },
        hoardsToday: {
            id: 'hoardsToday',
            title: 'Acopios del día',
            description: 'Monto y cantidad de acopios creados hoy',
            defaultSize: 's',
            sources: [SALES],
            linkPath: RoutePath.HOARDS,
            render: ({ sales, salesKpis, scope }) => (
                <WidgetCard title={isMine(scope) ? 'Mis acopios del día' : 'Acopios del día'} loading={sales.loading} error={sales.error} linkLabel="Ver acopios" linkPath={RoutePath.HOARDS}>
                    <KpiCard value={formatAmount(salesKpis.hoardsToday)} count={String(salesKpis.hoardsTodayCount)} label="acopios creados hoy" />
                </WidgetCard>
            )
        },
        budgetsMonth: {
            id: 'budgetsMonth',
            title: 'Presupuestos del mes',
            description: 'Cantidad de presupuestos generados en el mes',
            defaultSize: 's',
            sources: [SALES],
            linkPath: RoutePath.BUDGETS,
            render: ({ sales, salesKpis, scope }) => (
                <WidgetCard title={isMine(scope) ? 'Mis presupuestos del mes' : 'Presupuestos del mes'} loading={sales.loading} error={sales.error} linkLabel="Ver todos" linkPath={RoutePath.BUDGETS}>
                    <KpiCard value={String(salesKpis.budgetsMonth)} label="presupuestos generados" />
                </WidgetCard>
            )
        },
        salesChart: {
            id: 'salesChart',
            title: 'Evolución de ventas',
            description: 'Ventas + acopios de los últimos 12 meses',
            defaultSize: 'l',
            sources: [SALES],
            render: ({ sales, branches, scope, branchId }) => (
                <SalesChart
                    loading={sales.loading}
                    error={sales.error}
                    sales={sales.data?.sales || []}
                    hoards={sales.data?.hoards || []}
                    branches={scope === DashboardScope.COMPANY ? branches : branches.filter(branch => branch.id === branchId)}
                    isCurrentUser={isMine(scope)}
                />
            )
        },
        undeliveredSales: {
            id: 'undeliveredSales',
            title: 'Ventas sin entregar',
            description: 'Ventas pendientes de entrega',
            defaultSize: 's',
            sources: [SALES],
            linkPath: RoutePath.SALES,
            render: ({ sales, salesKpis, scope }) => (
                <WidgetCard title="Ventas sin entregar" loading={sales.loading} error={sales.error} linkLabel="Ver ventas" linkPath={RoutePath.SALES}>
                    <KpiCard value={String(salesKpis.undeliveredSalesCount)} label={isMine(scope) ? 'ventas propias pendientes de entrega' : 'ventas pendientes de entrega'} />
                </WidgetCard>
            )
        },
        expiringHoards: {
            id: 'expiringHoards',
            title: 'Acopios por vencer',
            description: 'Acopios sin entregar que vencen en los próximos 30 días o ya vencieron',
            defaultSize: 'm',
            sources: [SALES],
            linkPath: RoutePath.HOARDS,
            render: ({ sales }) => (
                <ExpiringHoardsWidget
                    loading={sales.loading}
                    error={sales.error}
                    hoards={sales.data?.hoards || []}
                    customers={sales.data?.customers || []}
                />
            )
        },
        sellerRanking: {
            id: 'sellerRanking',
            title: 'Ranking de vendedores',
            description: 'Vendedores ordenados por lo vendido en el mes (ventas + acopios)',
            defaultSize: 'm',
            sources: [SALES],
            render: ({ sales }) => (
                <SellerRankingWidget loading={sales.loading} error={sales.error} sales={sales.data?.sales || []} hoards={sales.data?.hoards || []} />
            )
        },
        paymentsMonth: {
            id: 'paymentsMonth',
            title: 'Cobros del mes',
            description: 'Lo cobrado en ventas y acopios en el mes, comparado con el mes anterior',
            defaultSize: 's',
            sources: [SALES],
            render: ({ sales, salesKpis }) => (
                <WidgetCard title="Cobros del mes" loading={sales.loading} error={sales.error}>
                    <KpiCard
                        value={formatAmount(salesKpis.paymentsMonth)}
                        label="cobrado en el mes"
                        trend={calcTrend(salesKpis.paymentsMonth, salesKpis.paymentsPrevMonth)}
                    />
                </WidgetCard>
            )
        },
        paymentsToday: {
            id: 'paymentsToday',
            title: 'Cobros del día',
            description: 'Lo cobrado hoy en ventas y acopios',
            defaultSize: 's',
            sources: [SALES],
            render: ({ sales, salesKpis }) => (
                <WidgetCard title="Cobros del día" loading={sales.loading} error={sales.error}>
                    <KpiCard value={formatAmount(salesKpis.paymentsToday)} label="cobrado hoy en ventas y acopios" />
                </WidgetCard>
            )
        },
        pendingCollections: {
            id: 'pendingCollections',
            title: 'Cobros pendientes',
            description: 'Deuda total de clientes: cuenta corriente y operaciones con saldo pendiente',
            defaultSize: 's',
            sources: [SALES],
            linkPath: `${RoutePath.CREDIT_ACCOUNTS}/${RoutePath.DEBTS}`,
            render: ({ sales, salesKpis }) => (
                <WidgetCard title="Cobros pendientes" loading={sales.loading} error={sales.error} linkLabel="Ver deudores" linkPath={`${RoutePath.CREDIT_ACCOUNTS}/${RoutePath.DEBTS}`}>
                    <KpiCard
                        value={formatAmount(salesKpis.pendingCollections)}
                        label="sin cobrar o cobrado parcialmente"
                        alert={salesKpis.hasOverdue ? '⚠ Hay cuentas corrientes vencidas' : undefined}
                    />
                </WidgetCard>
            )
        },
        topDebtors: {
            id: 'topDebtors',
            title: 'Top deudores',
            description: 'Los 5 clientes que más deben',
            defaultSize: 'm',
            sources: [SALES],
            linkPath: `${RoutePath.CREDIT_ACCOUNTS}/${RoutePath.DEBTS}`,
            render: ({ sales }) => (
                <TopDebtorsWidget
                    loading={sales.loading}
                    error={sales.error}
                    sales={sales.data?.sales || []}
                    hoards={sales.data?.hoards || []}
                    customers={sales.data?.customers || []}
                />
            )
        },

        // Finance
        cashBalance: {
            id: 'cashBalance',
            title: 'Balance de cajas',
            description: 'Disponible en cajas y lo que entró menos lo que salió hoy y en el mes',
            defaultSize: 's',
            sources: [FINANCE],
            linkPath: RoutePath.CASH_FUNDS,
            render: ({ finance }) => (
                <CashBalanceWidget
                    loading={finance.loading}
                    error={finance.error}
                    cashFunds={finance.data?.cashFunds || []}
                    monthTransactions={finance.data?.monthTransactions || []}
                />
            )
        },
        cashFunds: {
            id: 'cashFunds',
            title: 'Cajas activas',
            description: 'Saldo disponible de cada caja',
            defaultSize: 'm',
            sources: [FINANCE],
            linkPath: RoutePath.CASH_FUNDS,
            render: ({ finance }) => (
                <CashFundsWidget loading={finance.loading} error={finance.error} cashFunds={finance.data?.cashFunds || []} />
            )
        },
        expensesMonth: {
            id: 'expensesMonth',
            title: 'Gastos del mes',
            description: 'Gastos registrados en el mes',
            defaultSize: 's',
            sources: [FINANCE],
            linkPath: RoutePath.EXPENSES,
            render: ({ finance }) => {
                const currRange = getMonthRange(0)
                const expenses = (finance.data?.expenses || []).filter(expense => isInRange(expense.date, currRange))
                return (
                    <WidgetCard title="Gastos del mes" loading={finance.loading} error={finance.error} linkLabel="Ver gastos" linkPath={RoutePath.EXPENSES}>
                        <KpiCard
                            value={formatAmount(expenses.reduce((sum, expense) => sum + expense.amount, 0))}
                            count={String(expenses.length)}
                            label="gastos registrados"
                        />
                    </WidgetCard>
                )
            }
        },
        paymentOrders: {
            id: 'paymentOrders',
            title: 'Órdenes de pago pendientes',
            description: 'Órdenes de pago emitidas sin pagar',
            defaultSize: 's',
            sources: [FINANCE],
            linkPath: RoutePath.PAYMENT_ORDERS,
            render: ({ finance }) => {
                const paymentOrders = finance.data?.pendingPaymentOrders || []
                const total = paymentOrders.reduce((sum, paymentOrder) =>
                    sum + paymentOrder.paymentItems.reduce((itemsSum, paymentItem) => itemsSum + paymentItem.amount, 0)
                , 0)
                return (
                    <WidgetCard title="Órdenes de pago" loading={finance.loading} error={finance.error} linkLabel="Ver pagos" linkPath={RoutePath.PAYMENT_ORDERS}>
                        <KpiCard value={formatAmount(total)} count={String(paymentOrders.length)} label="emitidas sin pagar" />
                    </WidgetCard>
                )
            }
        },
        upcomingChecks: {
            id: 'upcomingChecks',
            title: 'Cheques próximos a vencer',
            description: 'Cheques que vencen en los próximos 7 días',
            defaultSize: 's',
            sources: [],
            linkPath: RoutePath.CHECKS,
            render: () => <ChecksAlertWidget />
        },

        // Stock and purchases
        criticalStock: {
            id: 'criticalStock',
            title: 'Stock crítico',
            description: 'Productos sin stock disponible o con stock negativo',
            defaultSize: 'm',
            sources: [],
            linkPath: RoutePath.STOCK,
            render: ({ branches, scope, branchId }) => (
                <CriticalStockWidget branches={branches} branchId={scope === DashboardScope.BRANCH ? branchId : undefined} />
            )
        },
        topRotation: {
            id: 'topRotation',
            title: 'Mayor rotación',
            description: 'Productos más vendidos de los últimos 90 días',
            defaultSize: 'm',
            sources: [SALES],
            linkPath: RoutePath.STOCK,
            render: ({ sales }) => (
                <TopRotationWidget loading={sales.loading} error={sales.error} sales={sales.data?.sales || []} />
            )
        },
        pendingPurchases: {
            id: 'pendingPurchases',
            title: 'Compras pendientes',
            description: 'Órdenes de compra sin recepcionar',
            defaultSize: 's',
            sources: [PURCHASES],
            linkPath: RoutePath.PURCHASES,
            render: ({ purchases }) => (
                <WidgetCard title="Compras pendientes" loading={purchases.loading} error={purchases.error} linkLabel="Ver compras" linkPath={RoutePath.PURCHASES}>
                    <KpiCard value={String(purchases.data?.pendingPurchases.length || 0)} label="órdenes de compra sin recepcionar" />
                </WidgetCard>
            )
        },
        overduePurchases: {
            id: 'overduePurchases',
            title: 'Compras atrasadas',
            description: 'Órdenes de compra con más de 7 días sin recepcionar',
            defaultSize: 'm',
            sources: [PURCHASES],
            linkPath: RoutePath.PURCHASES,
            render: ({ purchases }) => (
                <OverduePurchasesWidget
                    loading={purchases.loading}
                    error={purchases.error}
                    overduePurchases={purchases.data?.overduePurchases || []}
                    suppliers={purchases.data?.suppliers || []}
                    branches={purchases.data?.branches || []}
                />
            )
        },
        suppliersWithOrders: {
            id: 'suppliersWithOrders',
            title: 'Proveedores con pedidos',
            description: 'Proveedores con órdenes de compra pendientes',
            defaultSize: 's',
            sources: [PURCHASES],
            render: ({ purchases }) => (
                <WidgetCard title="Proveedores con pedidos" loading={purchases.loading} error={purchases.error}>
                    <KpiCard
                        value={String(new Set((purchases.data?.pendingPurchases || []).map(purchase => purchase.supplierId)).size)}
                        label="proveedores con órdenes pendientes"
                    />
                </WidgetCard>
            )
        },

        // Warehouse and logistics
        pendingReceptions: {
            id: 'pendingReceptions',
            title: 'Recepciones esperadas',
            description: 'Órdenes de compra sin recepcionar',
            defaultSize: 's',
            sources: [OPERATIONS],
            linkPath: RoutePath.RECEPTION_ORDERS,
            render: ({ operations }) => (
                <WidgetCard title="Recepciones esperadas" loading={operations.loading} error={operations.error} linkLabel="Ver recepciones" linkPath={RoutePath.RECEPTION_ORDERS}>
                    <KpiCard value={String(operations.data?.pendingReceptions.length || 0)} label="órdenes de compra sin recepcionar" />
                </WidgetCard>
            )
        },
        pickupsToday: {
            id: 'pickupsToday',
            title: 'Retiros pendientes hoy',
            description: 'Retiros de hoy sin completar en la sucursal',
            defaultSize: 's',
            sources: [WAREHOUSE],
            linkPath: RoutePath.PICKUP_ORDERS,
            render: ({ warehouse }) => (
                <WidgetCard title="Retiros pendientes hoy" loading={warehouse.loading} error={warehouse.error} linkLabel="Ver retiros" linkPath={RoutePath.PICKUP_ORDERS}>
                    <KpiCard value={String(warehouse.data?.retirosPendientes.length || 0)} label="retiros de hoy sin completar" />
                </WidgetCard>
            )
        },
        shipmentPreparation: {
            id: 'shipmentPreparation',
            title: 'Preparación de envíos hoy',
            description: 'Envíos de hoy en la sucursal: preparados y pendientes',
            defaultSize: 's',
            sources: [WAREHOUSE],
            linkPath: RoutePath.SHIPMENT_PREPARATION,
            render: ({ warehouse }) => {
                const shipments = warehouse.data?.enviosHoy || []
                const prepared = shipments.filter(shipment => shipment.deliveryStatus === DeliveryStatus.DELIVERED).length
                return (
                    <WidgetCard title="Preparación de envíos hoy" loading={warehouse.loading} error={warehouse.error} linkLabel="Ver preparación" linkPath={RoutePath.SHIPMENT_PREPARATION}>
                        <KpiCard
                            value={String(shipments.length)}
                            label={`${plural(prepared, 'preparado', 'preparados')} · ${plural(shipments.length - prepared, 'pendiente', 'pendientes')}`}
                        />
                    </WidgetCard>
                )
            }
        },
        pendingShipments: {
            id: 'pendingShipments',
            title: 'Envíos pendientes',
            description: 'Envíos pendientes o con entrega parcial',
            defaultSize: 's',
            sources: [OPERATIONS],
            linkPath: RoutePath.SHIPMENT_ORDERS,
            render: ({ operations }) => (
                <WidgetCard title="Envíos pendientes" loading={operations.loading} error={operations.error} linkLabel="Ver envíos" linkPath={RoutePath.SHIPMENT_ORDERS}>
                    <KpiCard value={String(operations.data?.pendingShipments.length || 0)} label="pendientes o con entrega parcial" />
                </WidgetCard>
            )
        },
        unassignedShipments: {
            id: 'unassignedShipments',
            title: 'Envíos sin hoja de ruta',
            description: 'Envíos pendientes que no están en ninguna hoja de ruta de hoy',
            defaultSize: 's',
            sources: [LOGISTICS],
            linkPath: RoutePath.SHIPMENT_ORDERS,
            render: ({ logistics }) => {
                const assigned = new Set((logistics.data?.roadmapLinks || []).map(link => link.deliveryId))
                const unassigned = (logistics.data?.shipmentDeliveries || []).filter(delivery => !assigned.has(delivery.id)).length
                return (
                    <WidgetCard title="Envíos sin hoja de ruta" loading={logistics.loading} error={logistics.error} linkLabel="Ver envíos" linkPath={RoutePath.SHIPMENT_ORDERS}>
                        <KpiCard
                            value={String(unassigned)}
                            label="envíos pendientes sin hoja de ruta"
                            alert={unassigned > 0 ? '⚠ Hay envíos sin asignar' : undefined}
                        />
                    </WidgetCard>
                )
            }
        },
        roadmapsToday: {
            id: 'roadmapsToday',
            title: 'Hojas de ruta de hoy',
            description: 'Hojas de ruta de hoy con su conductor, vehículo y entregas',
            defaultSize: 'm',
            sources: [LOGISTICS],
            linkPath: RoutePath.ROADMAPS,
            render: ({ logistics }) => (
                <RoadmapsTodayWidget
                    loading={logistics.loading}
                    error={logistics.error}
                    roadmaps={logistics.data?.todayRoadmaps || []}
                    roadmapLinks={logistics.data?.roadmapLinks || []}
                    drivers={logistics.data?.drivers || []}
                    vehicles={logistics.data?.vehicles || []}
                />
            )
        },
        activeVehicles: {
            id: 'activeVehicles',
            title: 'Vehículos activos',
            description: 'Vehículos disponibles',
            defaultSize: 's',
            sources: [LOGISTICS],
            linkPath: RoutePath.VEHICLES,
            render: ({ logistics }) => (
                <WidgetCard title="Vehículos activos" loading={logistics.loading} error={logistics.error} linkLabel="Ver vehículos" linkPath={RoutePath.VEHICLES}>
                    <KpiCard value={String(logistics.data?.vehicles.length || 0)} label="vehículos disponibles" />
                </WidgetCard>
            )
        }
    }
}
